import React from 'react';
import { useDispatch } from 'react-redux';
import { Popup } from '../Popup/Popup';
import {
  closeCheckoutModal,
  closePopupMessage,
  IPopupMessageConfig,
  POPUP_ACTION_ID,
  PopupActionType,
} from '@/store/reducers';
import { useNav } from '@/hooks/navigation/useNav.hook';
import { useCompleteOnboardingAndRedirect } from '@/hooks/onboarding/useCompleteOnboarding.hook';
import { POPUP_CONTENT_REGISTRY } from './popupContentRegistry';
import { DefaultPopupContent } from './DefaultPopupContent';

interface IPopupMessageContentProps {
  config: IPopupMessageConfig;
  hasQueuedFollowUp: boolean;
}

export const PopupMessageContent: React.FC<IPopupMessageContentProps> = ({
  config,
  hasQueuedFollowUp,
}) => {
  const dispatch = useDispatch();
  const completeOnboardingAndRedirect = useCompleteOnboardingAndRedirect();
  const { nav } = useNav();

  const executeButtonAction = () => {
    switch (config.buttonAction?.type) {
      case PopupActionType.RELOAD:
        window.location.reload();
        break;
      case PopupActionType.NAVIGATE:
        nav(config.buttonAction.url);
        break;
      case PopupActionType.CLOSE:
        if (config.actionId === POPUP_ACTION_ID.CLOSE_CHECKOUT_MODAL) {
          dispatch(closeCheckoutModal());
        }

        if (config.actionId === POPUP_ACTION_ID.COMPLETE_ONBOARDING) {
          completeOnboardingAndRedirect();
        }
        break;
    }
  };

  const dismiss = () => {
    dispatch(closePopupMessage());
  };

  // keep the backdrop up when advancing the queue — fading it out then
  // remounting the next popup makes the page flash between messages
  const closeOrAdvance = (animatedClose: () => void) => {
    if (hasQueuedFollowUp) {
      dismiss();
      return;
    }
    animatedClose();
  };

  const handleActionButtonClick = (animatedClose: () => void) => {
    executeButtonAction();
    closeOrAdvance(animatedClose);
  };

  return (
    <Popup
      onClose={dismiss}
      hideCloseButton={true}
      disableBackdropClose={true}
      zIndex={9999}
    >
      {({ close }) => {
        if (config.contentId) {
          const CustomContent = POPUP_CONTENT_REGISTRY[config.contentId];
          return (
            <CustomContent
              onAction={handleActionButtonClick}
              onDismiss={closeOrAdvance}
              close={close}
              showCloseButton={config.showCloseButton}
              buttonHierarchy={config.buttonHierarchy}
            />
          );
        }
        return (
          <DefaultPopupContent
            config={config}
            onAction={handleActionButtonClick}
            onDismiss={closeOrAdvance}
            close={close}
          />
        );
      }}
    </Popup>
  );
};
